import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { brand, nav } from "../data/site";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  // Transparent over the dark hero on the home page; solid everywhere else
  // (and once scrolled or the mobile menu is open) so the light logo stays legible.
  const isHome = location.pathname === "/";
  const solid = scrolled || open || !isHome;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        solid
          ? "bg-charcoal py-3 shadow-lg shadow-black/20"
          : "bg-transparent py-5"
      } ${scrolled || open ? "border-b border-gold/20" : "border-b border-transparent"}`}
    >
      <nav className="container-luxe flex items-center justify-between">
        {/* Left nav (desktop) */}
        <ul className="hidden flex-1 items-center gap-8 lg:flex">
          {nav.slice(0, 2).map((item) => (
            <li key={item.to}>
              <NavItem {...item} />
            </li>
          ))}
        </ul>

        {/* Logo */}
        <Link to="/" className="flex shrink-0 items-center justify-center">
          <img
            src={brand.logo}
            alt={brand.name}
            className={`w-auto transition-all duration-500 ${
              scrolled || open ? "h-16" : "h-24"
            }`}
          />
        </Link>

        {/* Right nav (desktop) */}
        <ul className="hidden flex-1 items-center justify-end gap-8 lg:flex">
          {nav.slice(2).map((item) => (
            <li key={item.to}>
              <NavItem {...item} />
            </li>
          ))}
        </ul>

        {/* Mobile toggle */}
        <button
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="ml-auto flex h-10 w-10 flex-col items-center justify-center gap-1.5 lg:hidden"
        >
          <span
            className={`h-px w-6 bg-cream transition-all duration-300 ${
              open ? "translate-y-[7px] rotate-45" : ""
            }`}
          />
          <span
            className={`h-px w-6 bg-cream transition-all duration-300 ${
              open ? "opacity-0" : ""
            }`}
          />
          <span
            className={`h-px w-6 bg-cream transition-all duration-300 ${
              open ? "-translate-y-[7px] -rotate-45" : ""
            }`}
          />
        </button>
      </nav>

      {/* Mobile menu */}
      <div
        className={`overflow-hidden transition-all duration-500 lg:hidden ${
          open ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <ul className="container-luxe flex flex-col gap-1 pb-6 pt-4">
          {nav.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                className="block py-3 font-serif text-2xl text-cream/90"
              >
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>
    </header>
  );
}

function NavItem({ to, label }) {
  return (
    <NavLink
      to={to}
      className={({ isActive }) =>
        `group relative font-sans text-xs uppercase tracking-[0.2em] transition-colors duration-300 ${
          isActive ? "text-gold" : "text-cream/80 hover:text-gold"
        }`
      }
    >
      {label}
      <span className="absolute -bottom-1 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
    </NavLink>
  );
}
